import { useCallback, useRef, useState } from 'react';
import type { CanvasMode, Diagram, ExcalidrawData, LastRequest, PendingImage, PipelineStage } from '../store/types.ts';
import { DEFAULT_MODEL, STAGE_LABELS } from '../store/types.ts';
import { generateWithClaude, generateExcalidrawWithClaude } from '../lib/claude-prompts.ts';
import { renderMermaidToSvg } from '../lib/mermaid-config.ts';

type APIHandlers = {
  onCommitCode: (diagramId: string, code: string, source: 'chat' | 'autofix', note: string) => void;
  onAppendChat: (diagramId: string, role: 'user' | 'assistant' | 'error', text: string) => void;
  onExcalidrawGenerated: (data: ExcalidrawData) => void;
  onPipelineChange: (stage: PipelineStage) => void;
  onStatusChange: (text: string) => void;
  onOpenSettings: () => void;
};

export function useClaudeAPI(
  apiKey: string,
  model: string,
  canvasMode: CanvasMode,
  selectedDiagram: Diagram | undefined,
  handlers: APIHandlers,
) {
  const [isSending, setIsSending] = useState(false);
  const [lastRequest, setLastRequest] = useState<LastRequest | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const startPipeline = useCallback(
    (stage: PipelineStage, statusOverride?: string) => {
      handlers.onPipelineChange(stage);
      handlers.onStatusChange(statusOverride || STAGE_LABELS[stage]);
    },
    [handlers],
  );

  const stopRequest = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const runClaudeGeneration = useCallback(
    async (request: LastRequest, mode: 'generate' | 'fix-only') => {
      if (!selectedDiagram) return;
      if (!apiKey.trim()) {
        handlers.onOpenSettings();
        handlers.onStatusChange('Add API key in Settings to continue');
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;
      setIsSending(true);

      try {
        startPipeline('sending');
        setTimeout(() => {
          if (abortRef.current === controller) startPipeline('generating');
        }, 120);

        // --- Draw mode: generate Excalidraw elements ---
        if (canvasMode === 'draw') {
          const skeletons = await generateExcalidrawWithClaude({
            apiKey: apiKey.trim(),
            model: model.trim() || DEFAULT_MODEL,
            prompt: request.prompt,
            currentElements: request.excalidrawElements || null,
            image: request.image,
            signal: controller.signal,
          });

          startPipeline('validating');

          // Convert skeletons to full Excalidraw elements via the public API
          const { convertToExcalidrawElements } = await import('@excalidraw/excalidraw');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const elements = convertToExcalidrawElements(skeletons as any);

          startPipeline('rendering');

          const data: ExcalidrawData = {
            elements: elements as unknown[],
            appState: {},
            files: {},
          };
          handlers.onExcalidrawGenerated(data);

          const userText = request.image ? `[image] ${request.image.name}\n${request.prompt}` : request.prompt;
          handlers.onAppendChat(selectedDiagram.id, 'user', userText);
          const elemCount = (elements as unknown[]).length;
          handlers.onAppendChat(
            selectedDiagram.id,
            'assistant',
            `Excalidraw diagram updated.\n---steps---\nGenerated ${elemCount} elements from prompt\nConverted skeletons to Excalidraw format\nRendered on canvas`,
          );

          handlers.onStatusChange('Diagram updated');
          handlers.onPipelineChange('completed');
          return undefined;
        }

        // --- Mermaid mode: existing flow ---
        let code = await generateWithClaude({
          apiKey: apiKey.trim(),
          model: model.trim() || DEFAULT_MODEL,
          prompt: request.prompt,
          currentCode: request.codeAtSubmit,
          image: request.image,
          signal: controller.signal,
        });

        startPipeline('validating');
        let validation = await renderMermaidToSvg(code, selectedDiagram.theme);

        if (validation.error && mode === 'generate') {
          const fixPrompt = `Fix the Mermaid syntax below and return corrected Mermaid only.\n\nError:\n${validation.error.raw}\n\nBroken code:\n${code}`;
          code = await generateWithClaude({
            apiKey: apiKey.trim(),
            model: model.trim() || DEFAULT_MODEL,
            prompt: fixPrompt,
            currentCode: code,
            image: null,
            signal: controller.signal,
          });
          validation = await renderMermaidToSvg(code, selectedDiagram.theme);
        }

        if (validation.error) throw new Error(`Mermaid invalid: ${validation.error.raw}`);

        startPipeline('rendering');

        // Detect diagram type from first line of generated code
        const firstLine = code.split('\n')[0]?.trim().toLowerCase() || '';
        const diagramType = firstLine.replace(/\s.*$/, '') || 'diagram';
        const hadExistingCode = Boolean(request.codeAtSubmit.trim());
        const wasAutoFixed = validation.error === null && mode === 'generate';

        if (mode === 'fix-only') {
          handlers.onCommitCode(selectedDiagram.id, code, 'autofix', 'Claude auto-fixed Mermaid syntax');
          handlers.onAppendChat(
            selectedDiagram.id,
            'assistant',
            `Auto-fix applied.\n---steps---\nAnalyzed syntax errors in current code\nGenerated corrected Mermaid\nValidated and rendered successfully`,
          );
        } else {
          handlers.onCommitCode(selectedDiagram.id, code, 'chat', 'Generated by Claude from prompt');
          const userText = request.image ? `[image] ${request.image.name}\n${request.prompt}` : request.prompt;
          handlers.onAppendChat(selectedDiagram.id, 'user', userText);

          const steps: string[] = [];
          if (request.image) steps.push('Analyzed attached image');
          steps.push(hadExistingCode ? `Updated existing ${diagramType}` : `Created new ${diagramType}`);
          if (wasAutoFixed) steps.push('First attempt had errors — auto-repaired');
          steps.push('Validated syntax and rendered on canvas');

          handlers.onAppendChat(
            selectedDiagram.id,
            'assistant',
            `Diagram updated.\n---steps---\n${steps.join('\n')}`,
          );
        }

        handlers.onStatusChange('Diagram updated');
        handlers.onPipelineChange('completed');
        return code;
      } catch (error) {
        const aborted = error instanceof DOMException && error.name === 'AbortError';
        if (aborted) {
          handlers.onPipelineChange('cancelled');
          handlers.onStatusChange('Generation cancelled');
        } else {
          handlers.onPipelineChange('error');
          handlers.onStatusChange('Request failed');
          if (selectedDiagram) {
            const message = error instanceof Error ? error.message : String(error);
            handlers.onAppendChat(selectedDiagram.id, 'error', message);
          }
        }
        return undefined;
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setIsSending(false);
      }
    },
    [apiKey, model, canvasMode, selectedDiagram, handlers, startPipeline],
  );

  const sendPrompt = useCallback(
    async (promptText: string, pendingImage: PendingImage | null, currentExcalidrawElements?: unknown[]) => {
      if (!selectedDiagram) return undefined;
      const text = promptText.trim();
      if (!text && !pendingImage) return undefined;

      const defaultPrompt = canvasMode === 'draw'
        ? 'Convert this attached image into an Excalidraw diagram.'
        : 'Convert this attached image into Mermaid syntax.';

      const request: LastRequest = {
        prompt: text || defaultPrompt,
        image: pendingImage ? { ...pendingImage } : null,
        codeAtSubmit: selectedDiagram.mermaidCode,
        excalidrawElements: currentExcalidrawElements,
      };

      setLastRequest(request);
      return runClaudeGeneration(request, 'generate');
    },
    [selectedDiagram, canvasMode, runClaudeGeneration],
  );

  const retryLast = useCallback(async () => {
    if (!lastRequest || isSending) return;
    await runClaudeGeneration(lastRequest, 'generate');
  }, [lastRequest, isSending, runClaudeGeneration]);

  const autoFix = useCallback(async () => {
    if (!selectedDiagram || isSending || !selectedDiagram.mermaidCode.trim()) return;
    const request: LastRequest = {
      prompt: 'Repair the current Mermaid syntax and return corrected Mermaid only.',
      image: null,
      codeAtSubmit: selectedDiagram.mermaidCode,
    };
    await runClaudeGeneration(request, 'fix-only');
  }, [selectedDiagram, isSending, runClaudeGeneration]);

  return { isSending, lastRequest, stopRequest, sendPrompt, retryLast, autoFix };
}
